import { Box, Button, Paper, Stack, Typography, useTheme } from "@mui/material";
import { MIN_TIME_STATIONS } from "../../../config/config";
import { CATEGORY_COLORS } from "../../../data/models/modules/ModuleModel";
import { StationOrderModel } from "../../../data/models/product/ProductModel";
import { useOrderTimer } from "../../hooks/useOrderTimer";

export interface StationOrderCardProps {
  orderStation: StationOrderModel;
  loadingId: string | null;
  doneStationOrder: (id: string) => Promise<void>;
}

export function StationOrderCard({
  orderStation,
  loadingId,
  doneStationOrder,
}: StationOrderCardProps) {
  const theme = useTheme();
  const elapsed = useOrderTimer(orderStation.createdAt);

  const minutes = Math.floor(elapsed / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (elapsed % 60).toString().padStart(2, "0");

  const shouldBlink = elapsed > MIN_TIME_STATIONS;

  return (
    <Paper
      sx={{
        p: 1,
        borderRadius: 1,
        boxShadow: "0px 3px 12px rgba(0,0,0,0.07)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        borderTop: "6px solid",
        borderTopColor: CATEGORY_COLORS[orderStation.moduleCategory],
      }}
    >
      <Stack direction="row" justifyContent="center" alignItems="center">
        <Typography fontWeight={700} fontSize={16}>
          {orderStation.moduleName}
        </Typography>
      </Stack>

      <Box>
        {orderStation.items
          .filter((i) => i.status === "PENDING")
          .map((i) => (
            <Box
              key={i.productId}
              sx={{
                py: 0.5,
                borderColor: theme.palette.divider,
                borderTop: "solid 1px",
              }}
            >
              <Typography fontWeight={500} fontSize={13}>
                {i.quantity} × {i.name}
              </Typography>

              {Boolean(i.comments) && (
                <Typography
                  color="text.secondary"
                  sx={{
                    backgroundColor: "rgba(255, 0, 0, 0.2)",
                    padding: "2px 8px",
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {i.comments}
                </Typography>
              )}
            </Box>
          ))}
      </Box>

      <Stack direction="row" gap={1}>
        <Button
          fullWidth
          variant="contained"
          color="success"
          disabled={loadingId === orderStation.id}
          onClick={async () => {
            await doneStationOrder(orderStation.id);
          }}
        >
          {loadingId === orderStation.id ? "Cargando..." : "Terminar"}
        </Button>
      </Stack>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 14,
          textAlign: "center",
          color: "#ffffff",
          width: "fit-content",
          m: "auto",
          px: 1.5,
          py: 0.5,
          borderRadius: "4px",
          backgroundColor: shouldBlink ? "#e53935" : "#43a047",
          animation: shouldBlink ? "blinkBg 1s infinite" : "none",
          "@keyframes blinkBg": {
            "0%": { opacity: 1 },
            "50%": { opacity: 0.4 },
            "100%": { opacity: 1 },
          },
        }}
      >
        {minutes}:{seconds}
      </Typography>
    </Paper>
  );
}
